import React from 'react';
import {
  Registry,
  RJSFSchema,
  UiSchema,
  getUiOptions,
  orderProperties,
} from '@rjsf/utils';
import { hasScalarListItems } from './blankListItems';

// One row per item for an array of objects, opted into by the connector with
// `"ui:options": {"layout": "row"}` on the array's ui schema:
//
//   [caption 1]  [caption 2]  …              (one line, from the item schema)
//   [control 1]  [control 2]  …  [toolbar]   (one line per item)
//
// Templates learn where they sit through RowLayoutContext rather than a flag
// written into the ui schema: rjsf hands each item `uiSchema.items` as-is, and
// flagging every property there would mean resolving the item schema (often a
// $ref) in generateUISchema. The context is set by CustomArrayFieldTemplate
// and read by RowAwareObjectFieldTemplate and HorizontalFieldTemplate:
//
//   'item' — the item object: no legend, its properties laid out as cells.
//   'cell' — a property of a row item: the control only (with its errors and
//            help); label and description live in the caption line.
//
// Every consumer resets the context for what it renders below, so anything
// nested inside a cell renders with the default templates.
export type RowLayoutRole = 'item' | 'cell' | null;
export const RowLayoutContext = React.createContext<RowLayoutRole>(null);

export type RowColumn = {
  name: string;
  title: React.ReactNode;
  required: boolean;
};

// The visible columns of a row-layout array, in render order, or null when the
// items can't be laid out as one row — they are not objects, or a property is
// itself an object or a list of objects (those keep the stacked layout, since a
// nested form does not fit in a cell).
export const rowColumns = (
  arraySchema: RJSFSchema,
  arrayUiSchema: UiSchema | undefined,
  registry: Registry,
): RowColumn[] | null => {
  const rawItems = arraySchema.items;
  if (!rawItems || typeof rawItems !== 'object' || Array.isArray(rawItems)) {
    return null;
  }
  const { schemaUtils } = registry;
  const itemSchema = schemaUtils.retrieveSchema(rawItems as RJSFSchema);
  const properties = itemSchema.properties;
  if (itemSchema.type !== 'object' || !properties) return null;
  const itemsUi: UiSchema = (arrayUiSchema?.items as UiSchema) || {};

  let order: string[];
  try {
    order = orderProperties(Object.keys(properties), itemsUi['ui:order']);
  } catch {
    return null;
  }

  const columns: RowColumn[] = [];
  for (const name of order) {
    const propUi: UiSchema = (itemsUi[name] as UiSchema) || {};
    if (propUi['ui:widget'] === 'hidden') continue;
    const propSchema = schemaUtils.retrieveSchema(
      properties[name] as RJSFSchema,
    );
    if (propSchema.type === 'object') return null;
    if (propSchema.type === 'array') {
      const items = propSchema.items;
      if (
        !items ||
        typeof items !== 'object' ||
        Array.isArray(items) ||
        !hasScalarListItems(schemaUtils.retrieveSchema(items as RJSFSchema))
      ) {
        return null;
      }
    }
    columns.push({
      name,
      title: getUiOptions(propUi).title ?? propSchema.title ?? name,
      required: !!itemSchema.required?.includes(name),
    });
  }
  return columns.length ? columns : null;
};

// Shared by the caption line and the item cells so they line up. Two columns
// (the annotations case: a term, then its values as pills) give the first a
// fixed 40% and the second the rest; otherwise columns share the row equally.
// Below `sm` the cells stack, so the widths only apply from `sm` up.
export const rowCellClass = (index: number, count: number): string =>
  count === 2 && index === 0
    ? 'min-w-0 sm:shrink-0 sm:grow-0 sm:basis-2/5'
    : 'min-w-0 sm:flex-1';
